// frontend/src/components/TransactionType/TransactionTypeList.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Edit, Trash2, Plus, Building, User, Receipt,
  ArrowRightLeft, FileText, Filter, Search,
  ChevronDown, AlertCircle, Loader2, Eye,
  Globe, Lock, Unlock, Download, Upload,
  Package, ShoppingCart, Truck, RefreshCw, DollarSign,
  TrendingUp, TrendingDown, Layers, CheckCircle2, Info
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import "./TransactionTypeList.css";

const url = process.env.REACT_APP_BACKEND_URL;

const TransactionTypeList = () => {
  const { currentUser, userType, company } = useAuth();
  const navigate = useNavigate();

  const [transactionTypes, setTransactionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedSign, setSelectedSign] = useState("");
  const [selectedReferenceType, setSelectedReferenceType] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [installingStandards, setInstallingStandards] = useState(false);

  // Banner shown after the standard install completes
  const [lastInstallSummary, setLastInstallSummary] = useState(null);

  // Reference types for the filter dropdown
  const referenceTypes = [...new Set(transactionTypes.map(t => t.reference_type))].filter(Boolean);

  // Icon mapping for transaction types
  const getIconForType = (type) => {
    const typeLower = type?.toLowerCase() || '';
    if (typeLower.includes('opening')) return Package;
    if (typeLower.includes('purchase') && !typeLower.includes('return')) return ShoppingCart;
    if (typeLower.includes('sale') && !typeLower.includes('return') && !typeLower.includes('order')) return DollarSign;
    if (typeLower.includes('return')) return RefreshCw;
    if (typeLower.includes('transfer')) return Truck;
    if (typeLower.includes('order')) return FileText;
    if (typeLower.includes('adjustment_in')) return TrendingUp;
    if (typeLower.includes('adjustment_out')) return TrendingDown;
    if (typeLower.includes('count_correction')) return Layers;
    return Receipt;
  };

  // Category based on transaction type
  const getCategory = (type) => {
    const typeLower = type?.toLowerCase() || '';
    if (typeLower.includes('opening') || typeLower.includes('purchase') || typeLower.includes('sale') || typeLower.includes('return')) {
      return 'stock';
    }
    if (typeLower.includes('transfer')) return 'transfer';
    if (typeLower.includes('order')) return 'order';
    if (typeLower.includes('adjustment') || typeLower.includes('count') || typeLower.includes('correction')) return 'adjustment';
    return 'other';
  };

  const categoryOptions = [
    { value: 'stock', label: 'Stock Management', icon: Package },
    { value: 'adjustment', label: 'Stock Adjustments', icon: TrendingUp },
    { value: 'transfer', label: 'Transfers', icon: Truck },
    { value: 'order', label: 'Orders', icon: FileText },
    { value: 'other', label: 'Other', icon: Receipt }
  ];

  // Permissions
  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  const canCreate = isSystemAdmin || (userType === 'company' && currentUser?.role === 'admin');

  useEffect(() => {
    fetchTransactionTypes();

    if (isSystemAdmin) {
      fetchCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, selectedSign, selectedReferenceType]);

  const fetchTransactionTypes = async () => {
    try {
      setLoading(true);

      const params = {};
      if (selectedSign) params.sign = selectedSign;
      if (selectedReferenceType) params.reference_type = selectedReferenceType;
      if (isSystemAdmin && selectedCompany) params.company_id = selectedCompany;

      const response = await axios.get(`${url}/api/transactiontype`, { params });

      if (response.data.success) {
        setTransactionTypes(response.data.data || []);
      } else {
        toast.error(response.data.message || "Failed to fetch transaction types");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch transaction types");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await axios.get(`${url}/api/company`);

      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load companies");
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleDelete = async (id, name, isSystemDefault) => {
    if (isSystemDefault) {
      toast.error("Cannot delete system default transaction types");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await axios.delete(`${url}/api/transactiontype/${id}`);

      if (response.data.success) {
        toast.success(`"${name}" deleted successfully`);
        fetchTransactionTypes();
      } else {
        toast.error(response.data.message || "Failed to delete transaction type");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Failed to delete transaction type";
      toast.error(errorMsg);
    }
  };

  const handleViewDetails = (transactionType) => {
    const modalContent = `
Transaction Type Details:

Type: ${transactionType.transaction_type}
Reference: ${transactionType.reference_type}
Sign: ${transactionType.sign} (${transactionType.sign === '+' ? 'Increases Stock' : transactionType.sign === '-' ? 'Decreases Stock' : 'No Stock Effect'})
Description: ${transactionType.notes || "N/A"}

Company: ${transactionType.company_id?.company_name}
Created By: ${transactionType.created_by?.name || "System"}
Created: ${new Date(transactionType.created_at).toLocaleDateString()}
Status: ${transactionType.status}
System Default: ${transactionType.is_system_default ? "Yes" : "No"}
${transactionType.updated_by ? `Last Updated: ${transactionType.updated_by?.name} on ${new Date(transactionType.updated_at).toLocaleDateString()}` : ''}
    `;

    alert(modalContent);
  };

  const handleEdit = (id) => {
    navigate(`/transactiontype/edit/${id}`);
  };

  const handleStatusToggle = async (id, currentStatus, name) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    const confirmMessage = `Are you sure you want to ${newStatus === "active" ? "activate" : "deactivate"} "${name}"?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      const response = await axios.put(
        `${url}/api/transactiontype/${id}`,
        { status: newStatus }
      );

      if (response.data.success) {
        toast.success(`Transaction type ${newStatus === "active" ? "activated" : "deactivated"} successfully`);
        fetchTransactionTypes();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update status");
    }
  };

  // ----------------------------------------------------------------
  // Install standard types — refreshes the current list (this page
  // IS /ViewTransactionType) and shows what actually happened.
  // ----------------------------------------------------------------
  const handleInstallStandardTypes = async () => {
    if (!canCreate) {
      toast.error("Only company administrators and system administrators can install standard transaction types");
      return;
    }

    if (isSystemAdmin && !selectedCompany) {
      toast.error("Please select a company first");
      return;
    }

    const companyName = isSystemAdmin
      ? companies.find((item) => item._id === selectedCompany)?.company_name
      : company?.company_name || "your company";

    const confirmed = window.confirm(
      `Install all standard inventory transaction types for ${companyName}?\n\n` +
      "This is safe to run again: missing types are created and existing standard types are repaired without duplicates."
    );

    if (!confirmed) return;

    try {
      setInstallingStandards(true);
      setLastInstallSummary(null);

      const response = await axios.post(
        `${url}/api/transactiontype/standard-setup`,
        isSystemAdmin ? { company_id: selectedCompany } : {}
      );

      if (response.data.success) {
        const summary = response.data.summary || {};

        const parts = [];
        if (summary.created) parts.push(`${summary.created} created`);
        if (summary.repaired) parts.push(`${summary.repaired} repaired`);
        if (summary.renamed) parts.push(`${summary.renamed} renamed`);
        if (summary.merged) parts.push(`${summary.merged} merged`);
        if (summary.unchanged) parts.push(`${summary.unchanged} already correct`);

        const summaryText = parts.length
          ? parts.join(" · ")
          : "No changes were necessary";

        const nothingChanged =
          !summary.created &&
          !summary.repaired &&
          !summary.renamed &&
          !summary.merged;

        if (nothingChanged) {
          toast.info(
            `Standard transaction types were already installed. ${summaryText}.`,
          );
        } else {
          toast.success(
            `Standard transaction types ready — ${summaryText}.`,
          );
        }

        setLastInstallSummary({
          companyName,
          at: new Date(),
          created: summary.created || 0,
          repaired: summary.repaired || 0,
          renamed: summary.renamed || 0,
          merged: summary.merged || 0,
          unchanged: summary.unchanged || 0,
        });

        await fetchTransactionTypes();
      } else {
        toast.error(
          response.data.message || "Failed to install standard transaction types",
        );
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        "Failed to install standard transaction types"
      );
    } finally {
      setInstallingStandards(false);
    }
  };

  // Filter transaction types based on search term and category
  const filteredTransactionTypes = transactionTypes.filter(type => {
    const matchesSearch =
      type.transaction_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      type.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      type.reference_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      type.company_id?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      type.created_by?.name?.toLowerCase().includes(searchTerm.toLowerCase());

    const category = getCategory(type.transaction_type);
    const matchesCategory = selectedCategory ? category === selectedCategory : true;

    return matchesSearch && matchesCategory;
  });

  // Stats
  const stats = {
    total: transactionTypes.length,
    active: transactionTypes.filter(t => t.status === "active").length,
    positive: transactionTypes.filter(t => t.sign === "+").length,
    negative: transactionTypes.filter(t => t.sign === "-").length,
    neutral: transactionTypes.filter(t => t.sign === "0").length,
    systemDefaults: transactionTypes.filter(t => t.is_system_default).length,
    adjustments: transactionTypes.filter(t => getCategory(t.transaction_type) === 'adjustment').length
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="animate-spin" size={32} />
        <p>Loading transaction types...</p>
      </div>
    );
  }

  return (
    <div className="transactiontype-container">
      {/* Header */}
      <div className="header">
        <div>
          <h2>Transaction Types</h2>
          <p className="company-info">
            {isSystemAdmin ? (
              <>
                <Globe size={14} /> All Companies
              </>
            ) : (
              <>
                <Building size={14} /> {company?.company_name} ({company?.company_code})
              </>
            )}
          </p>
        </div>

        <div className="header-actions">
          {/* One-click standard setup for company and system administrators */}
          {canCreate && (
            <button
              className="system-defaults-btn"
              onClick={handleInstallStandardTypes}
              disabled={installingStandards || (isSystemAdmin && !selectedCompany)}
              title={
                isSystemAdmin && !selectedCompany
                  ? "Select a company first"
                  : "Create or repair all standard transaction types in one action"
              }
            >
              {installingStandards ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Installing…
                </>
              ) : (
                <>
                  <Upload size={16} /> Install standard types
                </>
              )}
            </button>
          )}

          {/* Filter Toggle */}
          <button
            className="filter-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            Filter
          </button>

          {/* Add Button */}
          {canCreate && (
            <button
              onClick={() => navigate("/AddTransactionType")}
              className="add-button"
            >
              <Plus size={16} /> Add Transaction Type
            </button>
          )}
        </div>
      </div>

      {/* Install summary banner (shown after the standard-setup call) */}
      {lastInstallSummary && (
        <div
          style={{
            margin: "10px 0 0",
            padding: "10px 14px",
            background:
              lastInstallSummary.created +
              lastInstallSummary.repaired +
              lastInstallSummary.renamed +
              lastInstallSummary.merged >
              0
                ? "#f6ffed"
                : "#e6f7ff",
            border: `1px solid ${
              lastInstallSummary.created +
              lastInstallSummary.repaired +
              lastInstallSummary.renamed +
              lastInstallSummary.merged >
              0
                ? "#b7eb8f"
                : "#91d5ff"
            }`,
            borderRadius: 6,
            color:
              lastInstallSummary.created +
              lastInstallSummary.repaired +
              lastInstallSummary.renamed +
              lastInstallSummary.merged >
              0
                ? "#135200"
                : "#0050b3",
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <CheckCircle2 size={16} />
          <span>
            <strong>Standard types installed for {lastInstallSummary.companyName}</strong> —{" "}
            {lastInstallSummary.created} created,{" "}
            {lastInstallSummary.repaired} repaired,{" "}
            {lastInstallSummary.renamed} renamed,{" "}
            {lastInstallSummary.merged} merged,{" "}
            {lastInstallSummary.unchanged} already correct.
          </span>
          <span style={{ marginLeft: "auto", opacity: 0.7 }}>
            {lastInstallSummary.at.toLocaleTimeString()}
          </span>
          <button
            type="button"
            onClick={() => setLastInstallSummary(null)}
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              cursor: "pointer",
              padding: 0,
              fontWeight: 700,
            }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-label">Total:</span>
          <span className="stat-value">{stats.total}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Active:</span>
          <span className="stat-value active">{stats.active}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">+ Sign:</span>
          <span className="stat-value positive">{stats.positive}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">- Sign:</span>
          <span className="stat-value negative">{stats.negative}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">0 Sign:</span>
          <span className="stat-value neutral">{stats.neutral}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Adjustments:</span>
          <span className="stat-value adjustment">{stats.adjustments}</span>
        </div>
        {isSystemAdmin && (
          <div className="stat-item">
            <span className="stat-label">System Defaults:</span>
            <span className="stat-value system">{stats.systemDefaults}</span>
          </div>
        )}
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="filters-container">
          <div className="filters-grid">
            {isSystemAdmin && (
              <div className="filter-group">
                <label>Company:</label>
                <div className="select-wrapper">
                  <select
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                    disabled={loadingCompanies}
                  >
                    <option value="">All Companies</option>
                    {companies.map((comp) => (
                      <option key={comp._id} value={comp._id}>
                        {comp.company_name} ({comp.company_code})
                      </option>
                    ))}
                  </select>
                  <Building size={16} className="select-icon" />
                </div>
              </div>
            )}

            <div className="filter-group">
              <label>Category:</label>
              <div className="select-wrapper">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categoryOptions.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
                <Package size={16} className="select-icon" />
              </div>
            </div>

            <div className="filter-group">
              <label>Sign:</label>
              <div className="select-wrapper">
                <select
                  value={selectedSign}
                  onChange={(e) => setSelectedSign(e.target.value)}
                >
                  <option value="">All Signs</option>
                  <option value="+">+ (Increases Stock)</option>
                  <option value="-">- (Decreases Stock)</option>
                  <option value="0">0 (No Effect)</option>
                </select>
                <ArrowRightLeft size={16} className="select-icon" />
              </div>
            </div>

            <div className="filter-group">
              <label>Reference Type:</label>
              <div className="select-wrapper">
                <select
                  value={selectedReferenceType}
                  onChange={(e) => setSelectedReferenceType(e.target.value)}
                >
                  <option value="">All Types</option>
                  {referenceTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <FileText size={16} className="select-icon" />
              </div>
            </div>
          </div>

          <div className="filter-actions">
            <button
              className="clear-filters"
              onClick={() => {
                setSelectedCompany("");
                setSelectedCategory("");
                setSelectedSign("");
                setSelectedReferenceType("");
                setShowFilters(false);
              }}
            >
              Clear All Filters
            </button>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="search-container">
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search transaction types by name, notes, reference type, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="search-stats">
          <span>Showing: {filteredTransactionTypes.length} of {transactionTypes.length} transaction types</span>
          {(selectedCompany || selectedCategory || selectedSign || selectedReferenceType) && (
            <div className="active-filters">
              {selectedCompany && (
                <span className="filter-badge">
                  <Building size={12} />
                  {companies.find(c => c._id === selectedCompany)?.company_name || "Filtered"}
                </span>
              )}
              {selectedCategory && (
                <span className="filter-badge">
                  <Package size={12} />
                  {categoryOptions.find(c => c.value === selectedCategory)?.label}
                </span>
              )}
              {selectedSign && (
                <span className="filter-badge">
                  <ArrowRightLeft size={12} />
                  {selectedSign === "+" ? "Increases Stock" : selectedSign === "-" ? "Decreases Stock" : "No Effect"}
                </span>
              )}
              {selectedReferenceType && (
                <span className="filter-badge">
                  <FileText size={12} />
                  {selectedReferenceType}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="styled-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Transaction Type</th>
              <th>Reference Type</th>
              <th>Sign</th>
              <th>Notes</th>
              {isSystemAdmin && <th>Company</th>}
              <th>Created By</th>
              <th>Created Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactionTypes.length === 0 ? (
              <tr>
                <td colSpan={isSystemAdmin ? 10 : 9} className="no-data">
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Receipt size={48} color="#ccc" />
                    <p style={{ margin: '15px 0', color: '#666' }}>
                      {searchTerm || selectedCompany || selectedCategory || selectedSign || selectedReferenceType
                        ? "No transaction types found matching your criteria"
                        : "No transaction types found"
                      }
                    </p>
                    {canCreate && (
                      <button
                        className="add-button"
                        onClick={() => navigate("/AddTransactionType")}
                      >
                        <Plus size={16} /> Create First Transaction Type
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              filteredTransactionTypes.map((type, index) => {
                const Icon = getIconForType(type.transaction_type);
                const category = getCategory(type.transaction_type);
                const categoryInfo = categoryOptions.find(c => c.value === category);

                return (
                  <tr key={type._id} className={`category-${category}`}>
                    <td>{type.transactiontype_id || index + 1}</td>

                    <td>
                      <div className="transaction-type-cell">
                        <div className="type-header">
                          <Icon size={14} className="mr-2" />
                          <strong className="type-name">{type.transaction_type}</strong>
                          {categoryInfo && (
                            <span className="category-badge" title={categoryInfo.label}>
                              <categoryInfo.icon size={10} />
                            </span>
                          )}
                          {type.is_system_default && (
                            <span className="system-badge" title="System Default">
                              <Lock size={10} />
                            </span>
                          )}
                        </div>
                        {type.updated_by && (
                          <small className="updated-info">
                            Updated: {new Date(type.updated_at).toLocaleDateString()}
                          </small>
                        )}
                      </div>
                    </td>

                    <td>
                      <div className="reference-type-cell">
                        <span className="ref-badge">{type.reference_type}</span>
                      </div>
                    </td>

                    <td>
                      <div className={`sign-badge ${type.sign}`}>
                        <ArrowRightLeft size={12} className="mr-2" />
                        <span>{type.sign}</span>
                        <span className="sign-description">
                          {type.sign === "+" ? "Increases" :
                           type.sign === "-" ? "Decreases" :
                           "No Effect"}
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="notes-cell">
                        <span title={type.notes}>
                          {type.notes ? (
                            <>
                              {type.notes.length > 50 ? `${type.notes.substring(0, 50)}...` : type.notes}
                            </>
                          ) : (
                            <span className="text-muted">No description</span>
                          )}
                        </span>
                      </div>
                    </td>

                    {isSystemAdmin && (
                      <td>
                        <div className="company-cell">
                          <Building size={12} className="mr-2" />
                          <div>
                            <div>{type.company_id?.company_name || "N/A"}</div>
                            <small>{type.company_id?.company_code || ""}</small>
                          </div>
                        </div>
                      </td>
                    )}

                    <td>
                      <div className="user-cell">
                        <User size={12} className="mr-2" />
                        <div>
                          <div>{type.created_by?.name || "System"}</div>
                          <small>{type.created_by?.email || type.created_by?.Usercomp_email || ""}</small>
                        </div>
                      </div>
                    </td>

                    <td>{new Date(type.created_at).toLocaleDateString()}</td>

                    <td>
                      <span className={`status-badge ${type.status}`}>
                        {type.status}
                      </span>
                    </td>

                    <td>
                      <div className="action-buttons">
                        <button
                          className="action-btn view-btn"
                          onClick={() => handleViewDetails(type)}
                          title="View Details"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          className="action-btn edit-btn"
                          onClick={() => handleEdit(type._id)}
                          title="Edit Transaction Type"
                          disabled={type.is_system_default || type.status === "inactive"}
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className="action-btn status-btn"
                          onClick={() => handleStatusToggle(type._id, type.status, type.transaction_type)}
                          title={type.status === "active" ? "Deactivate" : "Activate"}
                          disabled={type.is_system_default}
                        >
                          {type.status === "active" ? <Unlock size={14} /> : <Lock size={14} />}
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => handleDelete(type._id, type.transaction_type, type.is_system_default)}
                          title="Delete Transaction Type"
                          disabled={type.is_system_default || type.status === "inactive"}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TransactionTypeList;